import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Key, Plus, Trash2, RefreshCw, Copy, CheckCircle, 
  XCircle, Search, Download, Edit2, Power, AlertCircle 
} from 'lucide-react';
import API_URL from '../config/API_URL';

// Toast Component
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={{
        position: 'fixed',
        top: '2rem',
        right: '2rem',
        background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        zIndex: 10000,
        border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
        minWidth: '300px',
        color: 'white',
      }}
    >
      {type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
      <span style={{ flex: 1, fontSize: '0.95rem', fontWeight: 500 }}>{message}</span>
    </motion.div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${color}30`,
        boxShadow: `0 4px 30px ${color}20`,
      }}
      whileHover={{ scale: 1.02, boxShadow: `0 8px 40px ${color}30` }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.75rem',
      }}>
        <span style={{
          fontSize: '0.9rem',
          color: '#a0a0c0',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontFamily: "'Rajdhani', sans-serif",
        }}>
          {title}
        </span>
        <Icon size={24} style={{ color: color }} />
      </div>
      
      <div style={{
        fontSize: '2.5rem',
        fontWeight: 900,
        fontFamily: "'Orbitron', monospace",
        background: `linear-gradient(135deg, ${color}, ${color}80)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}>
        {value}
      </div>
    </motion.div>
  );
};

// Generate Codes Modal
const GenerateCodesModal = ({ onClose, onGenerate }) => {
  const [count, setCount] = useState(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onGenerate(count, notes);
    setLoading(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={{
          background: 'rgba(20, 20, 35, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          border: '1px solid rgba(0, 255, 245, 0.3)',
          boxShadow: '0 30px 90px rgba(0, 255, 245, 0.2)',
        }}
      >
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#00fff5',
          marginBottom: '30px',
          fontFamily: "'Orbitron', monospace",
          textAlign: 'center',
        }}>
          Generate Access Codes
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#a0a0c0',
              marginBottom: '10px',
              fontFamily: "'Rajdhani', sans-serif",
            }}>
              Number of Codes (1-100)
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 245, 0.2)',
                borderRadius: '12px',
                fontSize: '16px',
                color: '#e0e0ff',
                outline: 'none',
                fontFamily: "'Rajdhani', sans-serif",
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#a0a0c0',
              marginBottom: '10px',
              fontFamily: "'Rajdhani', sans-serif",
            }}>
              Notes (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., For beta testers"
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 245, 0.2)',
                borderRadius: '12px',
                fontSize: '16px',
                color: '#e0e0ff',
                outline: 'none',
                fontFamily: "'Rajdhani', sans-serif",
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#a0a0c0',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px',
                background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Custom Code Modal
const CustomCodeModal = ({ onClose, onCreate }) => {
  const [code, setCode] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError('Code must be exactly 6 characters');
      return;
    }

    if (!/^[A-Z0-9]+$/.test(code)) {
      setError('Code must contain only letters and numbers');
      return;
    }

    setLoading(true);
    await onCreate(code, notes);
    setLoading(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={{
          background: 'rgba(20, 20, 35, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          border: '1px solid rgba(0, 255, 245, 0.3)',
          boxShadow: '0 30px 90px rgba(0, 255, 245, 0.2)',
        }}
      >
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#00fff5',
          marginBottom: '30px',
          fontFamily: "'Orbitron', monospace",
          textAlign: 'center',
        }}>
          Create Custom Code
        </h2>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{
              padding: '14px',
              borderRadius: '12px',
              fontSize: '14px',
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              color: '#ff3366',
              border: '1px solid #ff3366',
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '25px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#a0a0c0',
              marginBottom: '10px',
              fontFamily: "'Rajdhani', sans-serif",
            }}>
              Access Code (6 characters)
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={6}
              required
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 245, 0.2)',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '700',
                color: '#e0e0ff',
                outline: 'none',
                textAlign: 'center',
                letterSpacing: '4px',
                fontFamily: 'monospace',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#a0a0c0',
              marginBottom: '10px',
              fontFamily: "'Rajdhani', sans-serif",
            }}>
              Notes (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., For VIP users"
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 245, 0.2)',
                borderRadius: '12px',
                fontSize: '16px',
                color: '#e0e0ff',
                outline: 'none',
                fontFamily: "'Rajdhani', sans-serif",
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#a0a0c0',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                flex: 1,
                padding: '14px',
                background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer',
                opacity: (loading || code.length !== 6) ? 0.7 : 1,
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              {loading ? 'Creating...' : 'Create Code'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Access Codes Page
const AccessCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [systemEnabled, setSystemEnabled] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCodes(), loadStatus()]);
    } finally {
      setLoading(false);
    }
  };

  const loadCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/codes`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCodes(data.codes || []);
    } catch (error) {
      console.error('Failed to load codes:', error);
      showToastMessage('Failed to load codes', 'error');
    }
  };

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSystemEnabled(data.enabled);
      setStats({
        total: data.total_codes,
        used: data.used_codes,
        available: data.available_codes
      });
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  const toggleSystem = async () => {
    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !systemEnabled })
      });

      if (response.ok) {
        setSystemEnabled(!systemEnabled);
        showToastMessage(`Access code system ${!systemEnabled ? 'enabled' : 'disabled'}`, 'success');
      } else {
        throw new Error('Failed to toggle system');
      }
    } catch (error) {
      console.error('Failed to toggle system:', error);
      showToastMessage('Failed to toggle system', 'error');
    }
  };

  const generateCodes = async (count, notes) => {
    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/codes/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ count, notes })
      });

      const data = await response.json();

      if (response.ok) {
        showToastMessage(`Generated ${count} code(s) successfully`, 'success');
        loadData();
      } else {
        throw new Error(data.error || 'Failed to generate codes');
      }
    } catch (error) {
      console.error('Failed to generate codes:', error);
      showToastMessage(error.message, 'error');
    }
  };

  const createCustomCode = async (code, notes) => {
    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, notes })
      });

      const data = await response.json();

      if (response.ok) {
        showToastMessage('Custom code created successfully', 'success');
        loadData();
      } else {
        throw new Error(data.error || 'Failed to create code');
      }
    } catch (error) {
      console.error('Failed to create code:', error);
      showToastMessage(error.message, 'error');
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this code?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/codes/${codeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showToastMessage('Code deleted successfully', 'success');
        loadData();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete code');
      }
    } catch (error) {
      console.error('Failed to delete code:', error);
      showToastMessage(error.message, 'error');
    }
  };

  const bulkDelete = async () => {
    if (selectedCodes.length === 0) {
      showToastMessage('No codes selected', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedCodes.length} code(s)?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/access-codes/admin/codes/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code_ids: selectedCodes })
      });

      const data = await response.json();

      if (response.ok) {
        showToastMessage(`Deleted ${data.deleted_count} code(s)`, 'success');
        setSelectedCodes([]);
        loadData();
      } else {
        throw new Error(data.error || 'Failed to delete codes');
      }
    } catch (error) {
      console.error('Failed to delete codes:', error);
      showToastMessage(error.message, 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToastMessage('Copied to clipboard', 'success');
  };

  const exportCodes = () => {
    const csvContent = 'Code,Status,Notes,Created,Used By,Used At\n' +
      filteredCodes.map(code => {
        return `${code.code},${code.is_used ? 'Used' : 'Available'},"${code.notes || ''}",${code.created_at},${code.used_by?.name || ''},${code.used_at || ''}`;
      }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-codes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const showToastMessage = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const filteredCodes = codes.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (code.notes && code.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (code.used_by && code.used_by.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleCodeSelection = (codeId) => {
    setSelectedCodes(prev => 
      prev.includes(codeId) 
        ? prev.filter(id => id !== codeId)
        : [...prev, codeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCodes.length === filteredCodes.filter(c => !c.is_used).length) {
      setSelectedCodes([]);
    } else {
      setSelectedCodes(filteredCodes.filter(c => !c.is_used).map(c => c.id));
    }
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    systemToggle: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '1.5rem',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      marginBottom: '2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    controlsBar: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
    },
    searchBox: {
      flex: 1,
      minWidth: '250px',
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '0.875rem 1rem 0.875rem 3rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      borderRadius: '12px',
      color: '#e0e0ff',
      fontSize: '1rem',
      outline: 'none',
      fontFamily: "'Rajdhani', sans-serif",
      boxSizing: 'border-box',
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#00fff5',
    },
    button: {
      padding: '0.875rem 1.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#00fff5',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontFamily: "'Rajdhani', sans-serif",
    },
    table: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      overflow: 'hidden',
    },
    tableHeader: {
      background: 'rgba(0, 255, 245, 0.1)',
      padding: '1rem 1.5rem',
      display: 'grid',
      gridTemplateColumns: '50px 150px 1fr 150px 200px 100px',
      gap: '1rem',
      fontFamily: "'Orbitron', monospace",
      fontSize: '0.85rem',
      fontWeight: 700,
      color: '#00fff5',
      letterSpacing: '1px',
    },
    tableRow: {
      padding: '1rem 1.5rem',
      display: 'grid',
      gridTemplateColumns: '50px 150px 1fr 150px 200px 100px',
      gap: '1rem',
      borderBottom: '1px solid rgba(0, 255, 245, 0.1)',
      alignItems: 'center',
      fontFamily: "'Rajdhani', sans-serif",
      transition: 'all 0.3s ease',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#a0a0c0' }}>
          <RefreshCw size={48} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
          <div style={{ fontSize: '1.2rem', fontFamily: "'Rajdhani', sans-serif" }}>
            Loading access codes...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      {showGenerateModal && (
        <GenerateCodesModal
          onClose={() => setShowGenerateModal(false)}
          onGenerate={generateCodes}
        />
      )}

      {showCustomModal && (
        <CustomCodeModal
          onClose={() => setShowCustomModal(false)}
          onCreate={createCustomCode}
        />
      )}

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          ACCESS CODE MANAGEMENT
        </motion.h1>
        <p style={styles.subtitle}>
          Control registration access with invite codes
        </p>
      </div>

      {/* System Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.systemToggle}
      >
        <div>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#e0e0ff',
            marginBottom: '0.5rem',
            fontFamily: "'Orbitron', monospace",
          }}>
            Access Code System
          </div>
          <div style={{
            fontSize: '0.95rem',
            color: '#a0a0c0',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            {systemEnabled 
              ? 'Registration requires a valid access code'
              : 'Anyone can register without an access code'
            }
          </div>
        </div>
        <motion.button
          onClick={toggleSystem}
          style={{
            padding: '1rem 2rem',
            background: systemEnabled 
              ? 'linear-gradient(135deg, #00fff5, #8b5cf6)'
              : 'rgba(255, 255, 255, 0.1)',
            border: systemEnabled ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontFamily: "'Rajdhani', sans-serif",
            textTransform: 'uppercase',
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Power size={20} />
          {systemEnabled ? 'Enabled' : 'Disabled'}
        </motion.button>
      </motion.div>

      {/* Stats */}
      {stats && (
        <div style={styles.statsGrid}>
          <StatsCard
            title="Total Codes"
            value={stats.total}
            icon={Key}
            color="#00fff5"
          />
          <StatsCard
            title="Available"
            value={stats.available}
            icon={CheckCircle}
            color="#00ff88"
          />
          <StatsCard
            title="Used"
            value={stats.used}
            icon={AlertCircle}
            color="#ffaa00"
          />
        </div>
      )}

      {/* Controls */}
      <div style={styles.controlsBar}>
        <div style={styles.searchBox}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search codes, notes, or users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <motion.button
          style={styles.button}
          onClick={() => setShowGenerateModal(true)}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Plus size={18} />
          Generate
        </motion.button>

        <motion.button
          style={styles.button}
          onClick={() => setShowCustomModal(true)}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit2 size={18} />
          Custom
        </motion.button>

        <motion.button
          style={styles.button}
          onClick={exportCodes}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Download size={18} />
          Export
        </motion.button>

        <motion.button
          style={styles.button}
          onClick={loadData}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCw size={18} />
          Refresh
        </motion.button>

        {selectedCodes.length > 0 && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            style={{
              ...styles.button,
              border: '1px solid rgba(255, 51, 102, 0.3)',
              color: '#ff3366',
            }}
            onClick={bulkDelete}
            whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255, 51, 102, 0.4)' }}
            whileTap={{ scale: 0.95 }}
          >
            <Trash2 size={18} />
            Delete ({selectedCodes.length})
          </motion.button>
        )}
      </div>

      {/* Table */}
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <input
            type="checkbox"
            checked={selectedCodes.length === filteredCodes.filter(c => !c.is_used).length && filteredCodes.length > 0}
            onChange={toggleSelectAll}
            style={{ cursor: 'pointer' }}
          />
          <div>CODE</div>
          <div>NOTES</div>
          <div>STATUS</div>
          <div>USED BY</div>
          <div>ACTIONS</div>
        </div>

        {filteredCodes.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#a0a0c0',
          }}>
            <Key size={64} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
            <h3 style={{
              fontSize: '1.5rem',
              marginBottom: '0.5rem',
              fontFamily: "'Orbitron', monospace",
            }}>
              No codes found
            </h3>
            <p style={{ fontSize: '1rem', fontFamily: "'Rajdhani', sans-serif" }}>
              {searchTerm ? 'Try adjusting your search' : 'Generate some codes to get started'}
            </p>
          </div>
        ) : (
          filteredCodes.map((code, index) => (
            <motion.div
              key={code.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
              style={{
                ...styles.tableRow,
                opacity: code.is_used ? 0.5 : 1,
              }}
              whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            >
              <input
                type="checkbox"
                checked={selectedCodes.includes(code.id)}
                onChange={() => toggleCodeSelection(code.id)}
                disabled={code.is_used}
                style={{ cursor: code.is_used ? 'not-allowed' : 'pointer' }}
              />
              <div style={{
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#00fff5',
                letterSpacing: '2px',
              }}>
                {code.code}
              </div>
              <div style={{ color: '#a0a0c0', fontSize: '0.9rem' }}>
                {code.notes || '-'}
              </div>
              <div>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  background: code.is_used 
                    ? 'rgba(255, 170, 0, 0.2)' 
                    : 'rgba(0, 255, 136, 0.2)',
                  color: code.is_used ? '#ffaa00' : '#00ff88',
                  border: `1px solid ${code.is_used ? '#ffaa00' : '#00ff88'}`,
                }}>
                  {code.is_used ? 'USED' : 'AVAILABLE'}
                </span>
              </div>
              <div style={{ color: '#e0e0ff', fontSize: '0.9rem' }}>
                {code.used_by ? code.used_by.name : '-'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <motion.button
                  onClick={() => copyToClipboard(code.code)}
                  style={{
                    padding: '0.5rem',
                    background: 'transparent',
                    border: '1px solid rgba(0, 255, 245, 0.3)',
                    borderRadius: '8px',
                    color: '#00fff5',
                    cursor: 'pointer',
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Copy size={16} />
                </motion.button>
                {!code.is_used && (
                  <motion.button
                    onClick={() => deleteCode(code.id)}
                    style={{
                      padding: '0.5rem',
                      background: 'transparent',
                      border: '1px solid rgba(255, 51, 102, 0.3)',
                      borderRadius: '8px',
                      color: '#ff3366',
                      cursor: 'pointer',
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Trash2 size={16} />
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default AccessCodesPage;